import {
  IncentiveCategory,
  IncentiveLifecycleStatus,
  InlineAlertType,
} from '@varo/graphql-schema-types';
import backgroundTextureLavender from 'assets/images/vxl/component/productIntroScreen/background-texture-lavender.png';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import IncentiveErrorScreen from 'features/incentive/components/IncentiveErrorScreen/IncentiveErrorScreen';
import IncentiveProgramFallback from 'features/incentive/components/IncentiveProgramFallback/IncentiveProgramFallback';
import { INCENTIVE_HEADER_TOP_PADDING } from 'features/incentive/constants';
import { formatEntryCount } from 'features/incentive/helpers/formatEntryCount';
import { formatPrizeDate } from 'features/incentive/helpers/formatPrizeDate';
import { getIncentiveCopyPrefix } from 'features/incentive/helpers/getIncentiveCopyPrefix';
import { getNextDrawingPrizes } from 'features/incentive/helpers/getNextDrawingPrizes';
import {
  getIncentiveButtonAnalytics,
  IncentiveErrorSteps,
  IncentiveScreenNames,
} from 'features/incentive/helpers/incentiveAnalytics';
import { useIncentiveScreenViewed } from 'features/incentive/hooks/useIncentiveAnalytics';
import useIncentiveProgram from 'features/incentive/hooks/useIncentiveProgram';
import useIncentiveProgress from 'features/incentive/hooks/useIncentiveProgress';
import useRefreshOnRefocus from 'features/incentive/hooks/useRefreshOnRefocus';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { VaroPressableNames } from 'shared/analytics/types/pressableNames';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import { InlineAlert } from 'shared/components/vxl/InlineAlert/InlineAlert';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import theme from 'shared/styles/theme';

const CARD_BORDER_RADIUS = 4;
const ENTRY_ICON_SIZE = 16;

interface IncentiveTrackerScreenProps {
  incentiveType: IncentiveCategory;
  incentiveProgramId: string;
  lifecycleStatus: IncentiveLifecycleStatus;
}

function IncentiveTrackerScreen({
  incentiveType,
  incentiveProgramId,
  lifecycleStatus,
}: IncentiveTrackerScreenProps) {
  const router = useRouter();
  const { t: translatedText } = useTranslation('incentive', {
    keyPrefix: getIncentiveCopyPrefix(incentiveType, 'tracker'),
  });
  const programResult = useIncentiveProgram(incentiveType, incentiveProgramId);
  const {
    progress,
    fetching: isProgressFetching,
    error: progressRequestError,
    progressError,
    refresh: refreshProgress,
  } = useIncentiveProgress(incentiveProgramId);
  const { program, content } = programResult;

  useRefreshOnRefocus(refreshProgress);

  useIncentiveScreenViewed({
    screenName: IncentiveScreenNames.TRACKER,
    isVisible: Boolean(program && content && progress),
    incentiveType,
    incentiveProgramId,
    lifecycleStatus,
  });

  const handleHowItWorksPress = () => {
    router.push({
      pathname: '/incentive/[programId]/how-it-works',
      params: { programId: incentiveProgramId },
    });
  };

  if (!program || !content) {
    return (
      <IncentiveProgramFallback
        incentiveType={incentiveType}
        programResult={programResult}
      />
    );
  }

  if (!progress) {
    return progressError || progressRequestError ? (
      <IncentiveErrorScreen
        incentiveType={incentiveType}
        errorStep={IncentiveErrorSteps.PROGRESS}
        errorCode={progressError?.code}
        title={progressError?.title}
        message={progressError?.message}
        isRetrying={isProgressFetching}
        onRetry={refreshProgress}
      />
    ) : (
      <FullScreenLoading />
    );
  }

  const upcomingPrizes = getNextDrawingPrizes(
    content.content.prize_schedule,
    new Date(),
  );

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerTransparent: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={navigateBack}
              transparent
            />
          ),
        }}
      />
      <ProductIntroScreen
        visual={content.content.illustration}
        headlineTextStyle="expressive"
        backgroundTexture={backgroundTextureLavender}
        topPadding={INCENTIVE_HEADER_TOP_PADDING}
        eyebrowText={translatedText('eyebrow')}
        headlineText={translatedText('headline')}
        bodyContent={
          <View style={styles.bodyContainer}>
            <BodyBaseText
              numberOfLines={0}
              style={[styles.centeredText, styles.bodyText]}
            >
              {translatedText('body')}
            </BodyBaseText>
            {lifecycleStatus ===
            IncentiveLifecycleStatus.OptInPendingEligibility ? (
              <InlineAlert
                alertType={InlineAlertType.Brand}
                iconKeyType="CLOCK"
                bodyText={translatedText('pendingEligibility')}
              />
            ) : null}
            <View style={styles.card}>
              <View accessible style={styles.chancesSection}>
                <BodyBaseBoldText numberOfLines={0}>
                  {translatedText('chancesToWin')}
                </BodyBaseBoldText>
                <View style={styles.chancesCount}>
                  <Icon
                    iconKey="TICKET_FILLED"
                    iconSize={ENTRY_ICON_SIZE}
                    iconColor={theme.color.text.primary}
                  />
                  <AmplitudeMask>
                    <ExpressiveLgText
                      numberOfLines={1}
                      style={styles.chancesCountText}
                    >
                      {formatEntryCount(progress.cumulativeValue)}
                    </ExpressiveLgText>
                  </AmplitudeMask>
                </View>
              </View>
              {upcomingPrizes.length > 0 ? (
                <View style={styles.prizeSection}>
                  <BodyBaseBoldText role="heading" numberOfLines={0}>
                    {translatedText('nextGiveaway')}
                  </BodyBaseBoldText>
                  {upcomingPrizes.map((prize, index) => (
                    <View
                      key={`${prize.date}-${prize.label ?? ''}-${prize.amount_display}`}
                      accessible
                      style={[
                        styles.prizeRow,
                        index > 0 ? styles.prizeRowDivider : null,
                      ]}
                    >
                      <View style={styles.prizeDate}>
                        <BodyBaseText numberOfLines={0}>
                          {formatPrizeDate(prize.date)}
                        </BodyBaseText>
                        {prize.label ? (
                          <BodySmText
                            numberOfLines={0}
                            style={styles.secondaryText}
                          >
                            {prize.label}
                          </BodySmText>
                        ) : null}
                      </View>
                      <View style={styles.prizeAmount}>
                        <BodyBaseText numberOfLines={1}>
                          {prize.amount_display}
                        </BodyBaseText>
                        <BodySmText
                          numberOfLines={1}
                          style={styles.secondaryText}
                        >
                          {translatedText('winnerCount', {
                            count: prize.winner_count,
                          })}
                        </BodySmText>
                      </View>
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
            <BodySmText
              numberOfLines={0}
              style={[styles.centeredText, styles.secondaryText]}
            >
              {translatedText('updatesDaily')}
            </BodySmText>
          </View>
        }
        secondaryButton={{
          label: translatedText('howItWorks'),
          onPress: handleHowItWorksPress,
          componentAnalytics: getIncentiveButtonAnalytics(
            incentiveType,
            VaroPressableNames.INCENTIVE.INCENTIVE_HOW_IT_WORKS,
          ),
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  bodyContainer: {
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.l,
    paddingHorizontal: theme.spacing.m,
  },
  bodyText: {
    marginBottom: theme.spacing.s,
  },
  card: {
    backgroundColor: theme.color.background.tertiary,
    borderRadius: CARD_BORDER_RADIUS,
    overflow: 'hidden',
  },
  centeredText: {
    textAlign: 'center',
  },
  chancesCount: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.s,
  },
  chancesCountText: Platform.select({
    // Expressive native top padding offsets iOS line-height clipping; it pushes the digits low on Android.
    android: {
      includeFontPadding: false,
      paddingTop: 0,
      textAlignVertical: 'center',
    },
    default: {},
  }),
  chancesSection: {
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: theme.spacing.l,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  prizeAmount: {
    alignItems: 'flex-end',
  },
  prizeDate: {
    flex: 1,
  },
  prizeRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  prizeRowDivider: {
    borderTopColor: theme.color.border.primary,
    borderTopWidth: 1,
  },
  prizeSection: {
    borderTopColor: theme.color.border.primary,
    borderTopWidth: 1,
    paddingBottom: theme.spacing.s,
    paddingHorizontal: theme.spacing.l,
    paddingTop: theme.spacing.l,
  },
  secondaryText: {
    color: theme.color.text.secondary,
  },
});

export default IncentiveTrackerScreen;
